/**
 * AutocompleteInput — iter50b
 *
 * A thin wrapper around <input> that pops a prefix-match suggestion list
 * beneath the field. Free-form entry is always allowed — if the user
 * doesn't pick a suggestion, whatever they typed is submitted as-is
 * (letting downstream code decide "create new vs pick existing").
 *
 * Props:
 *   value: string
 *   onChange: (next: string) => void
 *   options: string[]              — full list of known values
 *   placeholder?: string
 *   testId?: string
 *   className?: string
 *   disabled?: boolean
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Plus } from "lucide-react";

/** Locale-aware prefix match (Turkish-friendly). */
function normalize(s) {
  return (s || "")
    .toString()
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export default function AutocompleteInput({
  value,
  onChange,
  options = [],
  placeholder,
  testId,
  className = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const wrapRef = useRef(null);

  // Prefix-first matches, then substring matches — up to 8 shown.
  const suggestions = useMemo(() => {
    const q = normalize(value);
    if (!q) return options.slice(0, 8);
    const prefix = [];
    const contains = [];
    for (const o of options) {
      const n = normalize(o);
      if (n.startsWith(q)) prefix.push(o);
      else if (n.includes(q)) contains.push(o);
    }
    return [...prefix, ...contains].slice(0, 8);
  }, [value, options]);

  // Reset focused index whenever the option pool changes.
  useEffect(() => {
    setFocusedIdx(0);
  }, [suggestions.length]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const exactMatch = options.find((o) => normalize(o) === normalize(value));
  const showCreateHint = value && value.trim() && !exactMatch;

  const commit = (val) => {
    onChange(val);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (suggestions[focusedIdx]) {
        e.preventDefault();
        commit(suggestions[focusedIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        className={[
          "flex w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-9",
          className,
        ].join(" ")}
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || showCreateHint) && (
        <div
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg"
          data-testid={testId ? `${testId}-menu` : undefined}
        >
          <ul className="max-h-56 overflow-y-auto py-1">
            {suggestions.map((opt, idx) => {
              const active = idx === focusedIdx;
              const isSelected = normalize(opt) === normalize(value);
              return (
                <li key={opt}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      // Use onMouseDown so we commit BEFORE the input's
                      // onBlur can close us.
                      e.preventDefault();
                      commit(opt);
                    }}
                    onMouseEnter={() => setFocusedIdx(idx)}
                    data-testid={testId ? `${testId}-option-${idx}` : undefined}
                    className={[
                      "flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm transition-colors",
                      active ? "bg-zinc-100" : "hover:bg-zinc-50",
                    ].join(" ")}
                  >
                    <span className="truncate">{opt}</span>
                    {isSelected ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
                    ) : null}
                  </button>
                </li>
              );
            })}
            {showCreateHint && (
              <li className="border-t border-zinc-100">
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-emerald-700"
                  data-testid={testId ? `${testId}-create-hint` : undefined}
                >
                  <Plus className="h-3 w-3" strokeWidth={2.5} />
                  Enter ile &ldquo;{value.trim()}&rdquo; adında yeni oluştur
                </div>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
